export type RuntimeErrorKind =
  | "chunk_load"
  | "sentiment_distribution"
  | "client_render"
  | "unhandled_rejection";

const runtimeErrorEventNames: Record<RuntimeErrorKind, string> = {
  chunk_load: "运行时错误：资源加载失败",
  sentiment_distribution: "运行时错误：舆情分布异常",
  client_render: "运行时错误：页面渲染异常",
  unhandled_rejection: "运行时错误：异步请求异常",
};

function messageFromUnknown(value: unknown): string {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "message" in value) {
    const message = (value as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
}

export function normalizeRuntimeErrorMessage(value: unknown): string {
  const message = messageFromUnknown(value).trim() || "unknown";
  return message.length > 120 ? message.slice(0, 120) : message;
}

export function classifyRuntimeError(
  error: unknown,
  fallbackKind: RuntimeErrorKind = "client_render"
): RuntimeErrorKind {
  const message = normalizeRuntimeErrorMessage(error).toLowerCase();

  if (
    message.includes("loading chunk") ||
    message.includes("chunkloaderror") ||
    message.includes("failed to fetch dynamically imported module")
  ) {
    return "chunk_load";
  }

  if (message.includes("reading 'positive'") || message.includes('reading "positive"')) {
    return "sentiment_distribution";
  }

  return fallbackKind;
}

export function shouldAutoReloadForChunkError(error: unknown): boolean {
  return classifyRuntimeError(error) === "chunk_load";
}

export function isExpectedApiRejection(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const maybe = error as { name?: unknown; status?: unknown; needRiskChallenge?: unknown };
  if (maybe.name !== "ApiRequestError") return false;
  return maybe.needRiskChallenge === true || maybe.status === 401 || maybe.status === 403;
}

export function reloadOnceForChunkError(storageKey = "weibotop:chunk-reload-at", windowMs = 60 * 1000): void {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(storageKey) || "0");
    if (Date.now() - lastReloadAt < windowMs) return;
    window.sessionStorage.setItem(storageKey, String(Date.now()));
    window.location.reload();
  } catch {
    window.location.reload();
  }
}

export function runtimeErrorEventName(kind: RuntimeErrorKind): string {
  return runtimeErrorEventNames[kind];
}
